/**
 * Sign-out state machine shared by all four apps.
 *
 * Exists because the four had four copies of `logout().catch(() => {})` —
 * each one clearing local state regardless of whether the server had
 * actually ended the session. When the production CSRF bug made every
 * logout 403, all four told the user "signed out" and none of them were.
 * One refresh put them straight back in.
 *
 * The rule this encodes: local state is cleared **only** after the server
 * confirms. A failed sign-out leaves the user signed in and says so.
 */
import { useCallback, useState } from 'react'
import { logout, logoutEverywhereElse } from '../api/client'

export interface SignOutMessages {
  /** Shown when the server did not end the session. */
  readonly failed: string
  /**
   * The three below are only needed by a caller that actually exposes
   * "sign out everywhere else" — in management that is Settings → Account,
   * and the user menu offers plain sign-out alone. Optional so a caller
   * without the action isn't made to invent strings it never shows.
   */
  readonly everywhereDone?: (count: number) => string
  readonly everywhereNone?: string
  readonly everywhereFailed?: string
}

export interface UseSignOutOptions {
  /**
   * Runs only after the server has confirmed the session is gone: drop
   * the cached profile, clear query caches, navigate to /login.
   */
  readonly onSignedOut: () => void
  readonly messages: SignOutMessages
}

export interface UseSignOut {
  readonly signOut: () => Promise<void>
  readonly signOutEverywhereElse: () => Promise<void>
  /** Non-null when the last action failed — render it next to the control. */
  readonly error: string | null
  /** Non-null after a successful "everywhere else". */
  readonly notice: string | null
  readonly busy: boolean
  readonly dismiss: () => void
}

export function useSignOut({ onSignedOut, messages }: UseSignOutOptions): UseSignOut {
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const dismiss = useCallback(() => {
    setError(null)
    setNotice(null)
  }, [])

  const doSignOut = useCallback(async () => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await logout()
    } catch {
      // The session is still live on the server. Clearing local state here
      // is what made the old bug invisible, so we deliberately do not.
      setError(messages.failed)
      return
    } finally {
      setBusy(false)
    }
    onSignedOut()
  }, [onSignedOut, messages.failed])

  const doSignOutEverywhereElse = useCallback(async () => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const { revokedCount } = await logoutEverywhereElse()
      setNotice(
        revokedCount > 0
          ? (messages.everywhereDone?.(revokedCount) ?? null)
          : (messages.everywhereNone ?? null),
      )
    } catch {
      setError(messages.everywhereFailed ?? messages.failed)
    } finally {
      setBusy(false)
    }
  }, [messages])

  return {
    signOut: doSignOut,
    signOutEverywhereElse: doSignOutEverywhereElse,
    error,
    notice,
    busy,
    dismiss,
  }
}
